import React, { useState } from 'react';
import {
  Typography,
  TextField,
  Button,
  Grid,
  DialogContent,
  DialogActions,
  DialogTitle,
  Box,
  Checkbox,
  FormControlLabel,
  Dialog,
  CircularProgress,
} from '@mui/material';
import ScanningDialog from './components/ScanningDialog';

const HomeConfirmationPage = ({ docType, file, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [openScanningDialog, setOpenScanningDialog] = useState(true);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [openInsuranceDialog, setOpenInsuranceDialog] = useState(true);

  const [formData, setFormData] = useState({
    insured: '',
    address: '',
    propertyType: '',
    yearBuilt: '',
    constructionType: '',
    squareFootage: '',
    effectiveDate: '',
    expiryDate: '',
    insurer: '',
    hasMortgage: false,
    mortgageCompany: '',
    policyNo: '',
    buildingLimit: '',
    contentsLimit: '',
    liabilityLimit: '',
    deductible: '',
    waterDamageDeductible: '',
    earthquakeDeductible: '',
  });

  const handleChange = (event) => {
    const { name, value, checked } = event.target;
    if (name === 'hasMortgage') {
      setFormData({
        ...formData,
        hasMortgage: checked,
        mortgageCompany: checked ? formData.mortgageCompany : '',
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const submitData = {
        ...formData,
        insuranceCompany: selectedInsurance?.id,
        applicationType: docType,
        applicationFile: file
      };
      
      // 模拟API调用
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      alert('Home insurance confirmation generated successfully!');
      onClose();
    } catch (error) {
      console.error('Failed to generate confirmation:', error);
      alert('Failed to generate confirmation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // 添加一个通用的 inputProps 配置
  const commonInputProps = {
    'aria-label': 'Required field',
    'title': 'Required field',
    'lang': 'en'
  };

  return (
    <>
      <ScanningDialog 
        open={openScanningDialog}
        onComplete={() => setOpenScanningDialog(false)}
      />

      {/* Loading Dialog */}
      <Dialog 
        open={loading}
        PaperProps={{
          sx: {
            borderRadius: 1,
            minWidth: '300px'
          }
        }}
      >
        <DialogContent sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          py: 4
        }}>
          <CircularProgress size={24} />
          <Typography>Generating Document...</Typography>
        </DialogContent>
      </Dialog>

      <DialogTitle>Property Insurance Confirmation</DialogTitle>
      
      <DialogContent>
        <Box sx={{ p: 2 }}>
          <Grid container spacing={2}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    px: 2,
                    color: '#1976d2',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}
                >
                  Property Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="insured"
                label="Insured Name"
                value={formData.insured}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="address"
                label="Property Address"
                value={formData.address}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="propertyType"
                label="Property Type"
                value={formData.propertyType}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="yearBuilt"
                label="Year Built"
                type="number"
                value={formData.yearBuilt}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="constructionType"
                label="Construction Type"
                value={formData.constructionType}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            {/* Policy Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    px: 2,
                    color: '#1976d2',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}
                >
                  Policy Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="effectiveDate"
                label="Effective Date"
                type="date"
                value={formData.effectiveDate}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="expiryDate"
                label="Expiry Date"
                type="date"
                value={formData.expiryDate}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="policyNo"
                label="Policy Number"
                value={formData.policyNo}
                onChange={handleChange}
                variant="outlined"
                size="small"
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.hasMortgage}
                    onChange={handleChange}
                    name="hasMortgage"
                  />
                }
                label="Property has mortgage"
              />
            </Grid>

            {formData.hasMortgage && (
              <Grid item xs={12} md={6}>
                <TextField
                  required
                  fullWidth
                  name="mortgageCompany"
                  label="Mortgage Company"
                  value={formData.mortgageCompany}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                  inputProps={commonInputProps}
                />
              </Grid>
            )}

            {/* Coverage Information Section */}
            <Grid item xs={12}>
              <Box sx={{ 
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                my: 1,
                '&::before, &::after': {
                  content: '""',
                  flex: 1,
                  borderBottom: '1px solid #e0e0e0'
                }
              }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    px: 2,
                    color: '#1976d2',
                    fontWeight: 500,
                    textAlign: 'center'
                  }}
                >
                  Coverage Information
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="buildingLimit"
                label="Building Limit"
                type="number"
                value={formData.buildingLimit}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="contentsLimit"
                label="Contents Limit"
                type="number"
                value={formData.contentsLimit}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                required
                fullWidth
                name="liabilityLimit"
                label="Liability Limit"
                type="number"
                value={formData.liabilityLimit}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="deductible"
                label="Standard Deductible"
                type="number"
                value={formData.deductible}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="waterDamageDeductible"
                label="Water Damage Deductible"
                type="number"
                value={formData.waterDamageDeductible}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                required
                fullWidth
                name="earthquakeDeductible"
                label="Earthquake Deductible"
                type="number"
                value={formData.earthquakeDeductible}
                onChange={handleChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <Typography sx={{ mr: 1 }}>$</Typography>,
                }}
                inputProps={commonInputProps}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions 
        sx={{ 
          p: 2,
          borderTop: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa'
        }}
      >
        <Button variant="outlined" onClick={onClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => handleSubmit(formData)}
          sx={{
            backgroundColor: '#1976d2',
            '&:hover': {
              backgroundColor: '#1565c0',
            },
          }}
        >
          Generate Confirmation
        </Button>
      </DialogActions>
    </>
  );
};

export default HomeConfirmationPage; 